import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Facebook, Instagram, MessageCircle, Link2, MoreHorizontal, Folder,
  Film, ExternalLink, Trash2, Eye,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function daysAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    return Math.max(0, Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
}

function PlatformIcon({ name }) {
  const p = (name || "").toLowerCase();
  const cls = "h-3 w-3";
  if (p.includes("facebook")) return <Facebook strokeWidth={1.5} className={cls} />;
  if (p.includes("instagram")) return <Instagram strokeWidth={1.5} className={cls} />;
  if (p.includes("messenger")) return <MessageCircle strokeWidth={1.5} className={cls} />;
  return <Link2 strokeWidth={1.5} className={cls} />;
}

function initials(nameOrEmail) {
  const src = (nameOrEmail || "").trim();
  if (!src) return "?";
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || src[0].toUpperCase();
}

export default function AdCard({ ad }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [avatarBroken, setAvatarBroken] = useState(false);

  const head = ad.head;
  const media = ad.media;
  // Prefer the MOST-RECENT image and video for this ad. Historical
  // rows from prior extension versions (v1.4.x saved multiple video
  // variants per ad) can otherwise cause .find() to pick a stale /
  // wrong-resolution URL. Sorting by created_at DESC guarantees the
  // latest successful save wins.
  const byCreatedDesc = (a, b) =>
    new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const image =
    [...media].filter((m) => m.media_type === "image").sort(byCreatedDesc)[0] ||
    head;
  const video = [...media]
    .filter((m) => m.media_type === "video")
    .sort(byCreatedDesc)[0];
  const platforms = head.metadata?.platforms || [];
  const days = daysAgo(head.created_at);
  const brand = head.page_name || "—";
  const pageAvatar = head.metadata?.page_avatar_url;
  const adId = head.ad_id || head.metadata?.ad_id;
  const rawSource = head.source_url || head.metadata?.source_url;
  // Prefer a per-ad Ad Library URL. If the stored source is the generic
  // search URL, rebuild it from ad_id so existing rows still link correctly.
  const sourceUrl = rawSource && /[?&]id=\d+/.test(rawSource)
    ? rawSource
    : adId ? `https://www.facebook.com/ads/library/?id=${adId}` : rawSource;
  const destinationUrl = head.metadata?.destination_url;

  const delMutation = useMutation({
    mutationFn: async () => {
      // Delete every media row that belongs to this ad
      const ids = media.map((m) => m.id);
      await Promise.all(ids.map((id) => api.delete(`/downloads/${id}`)));
    },
    onSuccess: () => {
      toast.success("Anúncio removido.");
      qc.invalidateQueries({ queryKey: ["downloads"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (e) => toast.error(e.message || "Falha ao remover."),
  });

  return (
    <article
      className="mb-4 break-inside-avoid rounded-xl border border-border bg-card shadow-[0_1px_0_hsl(var(--border))] hover:shadow-md hover:border-primary/30 transition-[box-shadow,border-color] duration-150"
      data-testid={`ad-card-${head.id}`}
    >
      {/* header */}
      <header className="flex items-center gap-2 px-3 py-2.5">
        <div className="h-8 w-8 shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary/60 grid place-items-center text-[11px] font-semibold text-primary-foreground">
          {pageAvatar && !avatarBroken ? (
            <img
              src={pageAvatar}
              alt={brand}
              className="h-full w-full object-cover"
              onError={() => setAvatarBroken(true)}
              data-testid={`ad-avatar-${head.id}`}
            />
          ) : (
            <span>{initials(brand)}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium truncate leading-tight" data-testid={`ad-brand-${head.id}`}>
            {brand}
          </div>
          <div className="text-[10px] text-muted-foreground truncate">
            {t("library.savedAgo", { n: days, unit: days === 1 ? t("library.day") : t("library.days") })}
          </div>
        </div>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
            data-testid={`ad-source-${head.id}`}
            title="Abrir no Ad Library"
            onClick={(e) => e.stopPropagation()}
          >
            <Link2 strokeWidth={1.5} className="h-3.5 w-3.5" />
          </a>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
              data-testid={`ad-menu-${head.id}`}
              aria-label="Menu"
            >
              <MoreHorizontal strokeWidth={1.5} className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => nav(`/history/${head.id}`)} data-testid={`ad-menu-open-${head.id}`}>
              <Eye strokeWidth={1.5} className="h-4 w-4" />
              Ver detalhes
            </DropdownMenuItem>
            {destinationUrl && (
              <DropdownMenuItem asChild data-testid={`ad-menu-visit-${head.id}`}>
                <a href={destinationUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink strokeWidth={1.5} className="h-4 w-4" />
                  Visitar site
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => delMutation.mutate()}
              disabled={delMutation.isPending}
              data-testid={`ad-menu-delete-${head.id}`}
            >
              <Trash2 strokeWidth={1.5} className="h-4 w-4" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* media */}
      <Link to={`/history/${head.id}`} className="block relative bg-secondary" data-testid={`ad-open-${head.id}`}>
        {video ? (
          <div className="relative">
            <video
              src={video.file_url}
              poster={video.thumbnail_url || image?.thumbnail_url}
              className="w-full h-auto block"
              muted
              playsInline
              preload="metadata"
            />
            <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/70 grid place-items-center text-white">
              <Film strokeWidth={1.5} className="h-3.5 w-3.5" />
            </div>
          </div>
        ) : image?.thumbnail_url || image?.file_url ? (
          <img
            src={image.thumbnail_url || image.file_url}
            alt={brand}
            className="w-full h-auto block"
            loading="lazy"
          />
        ) : (
          <div className="aspect-square" />
        )}
        {media.length > 1 && (
          <div className="absolute bottom-2 right-2 rounded-full bg-black/70 text-white text-[10px] font-medium px-2 py-0.5">
            +{media.length - 1}
          </div>
        )}
      </Link>

      {/* footer meta */}
      <div className="px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span>
            {days} {days === 1 ? t("library.day") : t("library.days")} · {t("library.active")}
          </span>
          {platforms.length > 0 && (
            <span className="ml-auto flex items-center gap-1 text-muted-foreground">
              {platforms.slice(0, 4).map((p) => (
                <PlatformIcon key={p} name={p} />
              ))}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`ad-badge-adlib-${head.id}`}
            >
              <Badge
                variant="secondary"
                className="rounded-md font-normal text-[10px] gap-1 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
              >
                <ExternalLink strokeWidth={1.5} className="h-2.5 w-2.5" />
                {t("library.adLibrary")}
              </Badge>
            </a>
          ) : (
            <Badge variant="secondary" className="rounded-md font-normal text-[10px] opacity-50">
              {t("library.adLibrary")}
            </Badge>
          )}
          {destinationUrl ? (
            <a
              href={destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`ad-badge-landing-${head.id}`}
            >
              <Badge
                variant="secondary"
                className="rounded-md font-normal text-[10px] gap-1 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
              >
                <ExternalLink strokeWidth={1.5} className="h-2.5 w-2.5" />
                {t("library.landing")}
              </Badge>
            </a>
          ) : (
            <Badge variant="secondary" className="rounded-md font-normal text-[10px] opacity-50">
              {t("library.landing")}
            </Badge>
          )}
        </div>

        <Button
          size="sm"
          className="w-full h-8 rounded-md font-semibold tracking-[0.14em] text-[11px]"
          data-testid={`ad-board-${head.id}`}
        >
          <Folder strokeWidth={1.5} className="h-3.5 w-3.5 mr-1.5" />
          {t("library.boards")}
        </Button>
      </div>
    </article>
  );
}
